import type { Messages } from '../messages.js'

/**
 * Korean. `수` for flips, which is the word a Korean board game already uses for a move: the
 * thing you spend, which is what the meter counts. `타일` for tiles and `판` for the board.
 *
 * Korean has one plural form and no agreement, so every count here is written with the counter
 * the noun takes — 단어 3개, 5수, 2점 — rather than with a plural.
 */
export const ko: Messages = {
  tag: 'ko',

  readingDictionary: '사전을 읽는 중…',
  noWordList: '"{language}" 단어 목록이 없습니다. 하나 만드세요:  pnpm dictionary build',
  emptyWordList: '"{language}" 단어 목록이 비어 있습니다.',

  flips: '수',
  score: '점수',
  words: '단어',
  round: '라운드',
  ticksLeftLabel: '이번 라운드에 남은 시간',
  typeAWord: '단어를 입력하세요',
  tapPrompt: '글자를 눌러 고르거나 되돌린 뒤 {action}',

  boardOfTiles: '타일 {n}개의 판',
  faceDown: '뒤집힘',
  wildCard: '조커',
  wildKey: '아무 글자나',
  letterReplaced: '{from}이(가) {to}(으)로 바뀌었습니다',
  letterSwap: '글자 교체!',
  spentTile: '사용한 타일',
  hiddenWhilePaused: '일시정지 중에는 가려집니다',
  letterInWord: '{letter}, 단어의 {position}번째 글자',

  completeWord: '단어 완성',

  completeShort: '완성',
  reset: '지우기',
  pause: '일시정지',
  resume: '계속',
  newGame: '새 게임',
  paused: '일시정지됨',
  outOfFlips: '수가 떨어졌습니다',
  finalResult: '{rounds} 동안 {words}로 {score}점',
  playAgain: '다시 하기',
  share: '공유',
  shareCopied: '복사했습니다.',
  shareSelect: '이것을 복사하세요:',

  lettersSelect: '글자로 고릅니다',
  keysWild: '어떤 타일에도 없는 글자를 입력하면 가져갑니다',
  clearsEvery: '선택한 {letter}를 모두 지웁니다',
  undoLastLetter: '마지막 글자 되돌리기',
  noWordsYet: '아직 단어가 없습니다.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: '이미 찾았습니다',
  reasonTooShort: '너무 짧습니다',
  reasonNotAWord: '단어가 아닙니다',
  reasonAllFound: '이미 전부 찾았습니다',
  noSuchLetterUp: '열린 {letter}이(가) 없습니다',
  nothingUp: '열리지 않았습니다',
  letterHidden: '글자 하나가 숨었습니다',
  shuffled: '섞었습니다',
  shuffledAndBilled: '섞었고, 쓰지 않은 {flips}를 청구했습니다',
  tooFewLetters: '남은 글자가 너무 적습니다',

  gameLanguage: '언어',
  interfaceLanguage: '화면',
  dictionarySize: '{full}개 중 일상 단어 {common}개',
  filterLanguages: '언어 검색',
  noMatches: '결과 없음',

  nerdMode: '너드 모드',
  rules: '규칙',
  difficulty: '난이도',
  difficultyNames: { easy: '쉬움', medium: '보통', hard: '어려움', insane: '미친' },
  pausedNoBoard: '이 게임은 순위표에 오르지 않습니다.',
  about: '정보',
  themeLabel: '테마',
  themeNames: { traditional: '기본', light: '밝게', contrast: '고대비' },
  tiles: '타일 (N)',
  secondsPerTick: '초 / 박자',
  holdTicks: '멈춤 박자',
  minWord: '가장 짧은 단어',
  startingFlips: '시작 수',
  wildChance: '조커 확률',
  replaceChance: '글자 교체 확률',
  hideChance: '글자 숨김 확률',
  wordCompleteMode: '단어를 완성하면',
  wordCompleteNames: { shuffle: '섞기', spend: '사용', keep: '유지' },
  flipEconomy: '수 경제',
  flipEconomyNames: {
    none: '없음',
    perLetter: '글자마다',
    fibonacci: '피보나치',
    overMinimum: '최소 초과분',
  },
  repeatedLetterKey: '같은 글자 키',
  keySchemeNames: { cycle: '순환', advance: '전진' },
  keySchemeHelp: {
    cycle:
      'ㄱ은 아직 쓰지 않은 다음 ㄱ을 가져오고, 모두 단어에 들어가면 지웁니다. ' +
      'Shift+ㄱ도 지웁니다.',
    advance: 'ㄱ은 아직 쓰지 않은 다음 ㄱ을 가져옵니다. Shift+ㄱ은 단어 속 ㄱ을 모두 지웁니다.',
  },

  whatThatMeans: '무슨 뜻인가',
  factRound: '라운드',
  factWholeBoardUp: '판 전체가 열려 있는 시간',
  factRoundCosts: '한 라운드의 비용',
  factFlipsBuy: '시작 수로 살 수 있는 것',
  factThisBoard: '이 판',
  factBoardHadToAdmit: '판이 받아들여야 했던 수',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, 가장 긴 것 {longest}',
  wordsIncludingOneOf: '{words}, 그중 하나는 {ceiling}',
  scorelessRounds: '{rounds} 무득점',

  whatAWordPays: '단어의 값어치',
  columnLetters: '글자',
  columnCost: '비용',
  columnPoints: '점수',
  columnFlips: '수',
  columnNet: '순이익',

  canonicalRules: '{difficulty} 기본 규칙입니다.',
  customRules: '기본값에서 바꿨습니다. 직접 정한 규칙의 점수는 순위에 오르지 않습니다.',
  applyAndStart: '적용하고 새 게임 시작',
  changesNextGame: '변경 사항은 다음 게임부터 적용됩니다.',
  presets: '기본값:',

  start: '시작',
  restart: '다시 시작',
  quit: '나가기',
  quitTitle: '이 게임에서 나갈까요?',
  restartTitle: '이 게임을 다시 시작할까요?',
  restartConfirm: '다시 시작',
  quitConfirm: '나가기',
  keepPlaying: '계속 하기',
  personalBest: '최고 기록',
  newPersonalBest: '새로운 개인 최고 기록입니다.',
  columnRank: '#',
  notRanked: '직접 정한 규칙이라 이 게임은 순위에 오르지 않습니다.',
  notRankedPaused: '시간이 멈춰서 이 게임은 순위에 오르지 않습니다.',
  rankOfTotal: '{total} 중 {rank}',

  howToPlay: '게임 방법',

  backToGame: '게임으로 돌아가기',
  welcomeTitle: 'Blinkered에 오신 것을 환영합니다',
  tutorialSkip: '건너뛰기',
  tutorialNext: '다음',
  tutorialBack: '이전',
  tutorialStart: '게임 시작',
  tutorialHideAgain: '다시 보지 않기',
  tutorialProgress: '{total} 중 {n}',
  tutorialSkipTitle: '안내를 건너뛸까요?',
  tutGoalTitle: 'Blinkered는 경주입니다',
  tutGoalBody:
    '게임은 정해진 만큼의 수를 가지고 시작하고, 타일이 하나 열릴 때마다 한 수가 듭니다. 수가 떨어지면 게임이 끝납니다. 단어를 만들면 수를 얻고, 긴 단어일수록 더 많은 수를 얻습니다.',
  tutSpentBody:
    '단어에 쓴 글자는 다음 라운드까지 뒤집혀 있습니다. 판의 글자 구성은 글자 교체로만 바뀝니다.',
  tutChangeTitle: '글자가 바뀔 수 있어요',
  tutSameLetters:
    '미처 못 쓴 단어가 보였나요? 같은 글자가 다음 라운드에 자리를 섞어 다시 나옵니다.',
  tutPickLetters: '원하는 글자를 순서대로 눌러 단어를 만드세요.',
  tutMoreTurn: '생각하는 동안에도 타일은 계속 열리니, 더 좋은 글자가 아직 나올 수 있습니다.',
  tutTapBack:
    '원하지 않은 글자를 눌렀나요? 다시 누르면 되돌아갑니다. 마지막 글자만이 아니라 어느 것이든요.',
  tutComplete: '단어가 준비되면 완성을 누르세요.',
  htBoardTitle: '판',
  htBoardBody: '타일은 무작위 순서로 하나씩 열립니다. 열린 타일로 단어를 만듭니다.',
  htWordsTitle: '단어',
  htWordsBody: '열린 타일의 글자를 순서대로 입력하거나 눌러서 단어를 만드세요.',
  htFlipsTitle: '수',
  htFlipsBody:
    '타일이 하나 열릴 때마다 한 수가 듭니다. 단어를 완성하면 수가 돌아오고, 긴 단어일수록 더 많이 돌아옵니다. 수가 떨어지면 게임이 끝납니다.',
  htRoundTitle: '라운드',
  htRoundBody:
    '라운드의 마지막 타일이 열리면 판 전체가 잠시 그대로 있습니다. 그런 다음 타일을 뒤집어 섞고 새 라운드가 시작됩니다.',
  htLanguagesTitle: '언어',
  htLanguagesBody:
    '{n}개 언어. 어떤 판이든 사람들이 실제로 쓰는 단어로 풀 수 있습니다. 드문 단어도 사전에 있으면 점수가 됩니다.',
  htKeysTitle: '키보드',
  htWildTitle: '조커',
  htWildBody:
    '가끔 글자 대신 조커가 나옵니다. 조커는 올바른 단어를 만드는 어떤 글자로도 칩니다. 이미 완성한 단어는 치지 않습니다.',
  htHideTitle: '숨는 글자',
  htHideBody:
    '어려운 단계에서는 열려 있던 글자가 스스로 다시 뒤집히고, 그 라운드 안에서 나중에 돌아옵니다. 그만큼 타이머 칸이 하나 늘어나며 빨간색으로 표시됩니다. 이미 고른 글자는 뒤집히지 않습니다.',
  tutHideBody:
    '어려운 단계에서는 열려 있던 글자가 스스로 다시 뒤집히고, 그 라운드 안에서 나중에 돌아옵니다. 이미 고른 글자는 뒤집히지 않습니다.',
  htSwapTitle: '바뀌는 글자',
  htSwapBody:
    '가끔 라운드 사이에 글자 하나가 다른 글자로 바뀝니다. 어떤 글자가 빠지고 어떤 글자가 들어왔는지 보여줍니다.',
  htLevelsTitle: '난이도',
  htLevelEasy:
    '게임 내내 같은 열두 글자라서 외워 두고 머릿속에 단어 목록을 담아 둘 수 있습니다. 타일이 천천히 열리고, 글자를 다 고를 때까지 판 전체가 충분히 오래 보입니다.',
  htLevelMedium:
    '이따금 글자 하나가 바뀌어서 나중에 쓰려던 단어를 기억하기가 어려워집니다. 볼 시간도 생각할 시간도 줄어듭니다.',
  htLevelHard:
    '세 글자 단어는 더 이상 세지 않고, 두 라운드마다 글자가 바뀝니다. 판이 겨우 열리자마자 섞입니다.',
  htLevelInsane: '전부 한꺼번에, 최고 속도로. 마지막 수가 끝나자마자 판이 섞입니다.',
  htTouchTitle: '터치스크린',
  htTouchBody:
    '열린 타일을 눌러 글자를 가져오세요. 가져온 글자를 누르면 되돌아갑니다. 완성과 지우기는 판 아래에 있습니다.',

  accountTitle: '내 계정',
  menuProfile: '내 프로필',
  menuGames: '내 게임',
  menuPublicPage: '내 공개 페이지',
  menuSignedInAs: '로그인 계정',
  signIn: '로그인',
  signOut: '로그아웃',
  signInTitle: '로그인 또는 가입',
  signInLead: '계정이 있으면 어느 기기에서든 게임이 남습니다.',
  signInKeepGame: '로그인하면 이 게임도, 이후의 게임도 남습니다.',
  continueWithApple: 'Apple로 계속하기',
  continueWithGoogle: 'Google로 계속하기',
  signInOr: '또는',
  emailLabel: '이메일',
  codeLabel: '코드',
  emailMeACode: '코드 보내주세요',
  working: '처리 중…',
  sendNewCode: '새 코드 보내기',
  codeSent: '메일을 확인하세요. 코드는 10분간 유효합니다.',
  notNow: '나중에',
  badEmail: '이메일 주소 같지 않습니다.',
  badCode: '이 코드는 맞지 않았습니다. 코드는 10분 뒤 만료되고 한 번만 쓸 수 있습니다.',
  serverBusy: '서버에 연결하지 못했습니다. 잠시 후 다시 시도하세요.',
  serverDown: '서버에 연결하지 못했습니다.',
  offline: '오프라인',
  leaderboardTitle: '순위표',
  leaderboardEmpty: '이 순위표에는 아직 기록이 없습니다. 첫 번째가 되어 보세요.',
  leaderboardWouldBe: '이 기록이 들어갈 자리',
  leaderboardThisGame: '이 기록',
  leaderboardRanksHere: '이 기록이 들어간 자리',
  leaderboardOutside: '상위 5위 밖입니다.',
  tutAccountTitle: '점수를 남기세요',
  tutAccountBody:
    '계정이 있으면 게임이 저장되고, 점수가 순위표에 오르며, 프로필을 관리할 수 있습니다.',
  ssoCancelled: '로그인을 취소했습니다.',
  ssoExpired: '너무 오래 걸렸습니다. 다시 로그인하세요.',
  ssoNoUsername: '계정 생성을 마치지 못했습니다. 잠시 후 다시 시도하세요.',
  ssoFailed: '되지 않았습니다. 다시 시도하거나 이메일을 쓰세요.',
  tabProfile: '프로필',
  countryAny: '밝히지 않음',
  findCountry: '국가 검색',
  usernameLabel: '사용자 이름',
  bioLabel: '소개',
  countryLabel: '국가',
  bioHint: '링크 불가. {left}/{max}',
  save: '저장',
  saving: '저장 중…',
  saved: '저장했습니다.',
  nameTaken: '이미 쓰고 있는 이름입니다.',
  nameTooShort: '조금 더 길게 해주세요.',
  nameTooLong: '너무 깁니다.',
  nameBadCharacters: '문자, 숫자, 하이픈, 밑줄만 됩니다.',
  nameBadEdges: '문자나 숫자로 시작하고 끝나야 합니다.',
  nameMixedScripts: '문자 체계를 하나만 쓰세요.',
  nameReserved: '저희가 나눠주는 이름과 비슷합니다. 다른 이름을 고르세요.',
  nameUnusable: '그 이름은 쓸 수 없습니다.',
  bioTooLong: '소개는 최대 {max}자입니다.',
  bioHasLink: '소개에는 링크를 넣을 수 없습니다.',
  bioHasControl: '소개에 들어갈 수 없는 문자가 있습니다.',
  bioUnusable: '그 소개는 쓸 수 없습니다.',
  gamesHeading: '게임',
  gamesEmpty: '아직 아무것도 없습니다. 로그인한 채로 한 게임이 여기 쌓입니다.',
  gamesEmptyShort: '아직 아무것도 없습니다.',
  gamesLoading: '게임을 읽는 중…',
  theirGamesLoading: '이 사람의 게임을 읽는 중…',
  profileLoading: '프로필을 읽는 중…',
  playerNotFound: '그런 이름을 쓰는 사람이 없습니다.',
  columnWhen: '언제',
  columnRounds: '라운드',
  columnGame: '게임',
  gameLoading: '게임을 읽는 중…',
  gameUnreadable: '이 게임을 읽지 못했습니다.',
  gameNotRanked: '규칙을 바꿔서 한 게임이라 순위에 들지 않습니다.',
  detailNotKept: '이 게임의 단어와 보드는 더 이상 보관하지 않습니다.',
  openingBoard: '첫 보드',
  roundByRound: '라운드별',
  roundNothing: '찾은 것 없음',
  changeReplaced: '{slot}번 자리가 {letter}로 바뀜',
  changeWildOn: '{slot}번 자리의 {letter}가 와일드',
  changeWildOff: '{slot}번 자리 {letter}의 와일드가 사라짐',
  keepThisGame: '이 게임 남기기',
  savedToYourGames: '게임에 저장했습니다.',

  reportThis: '신고',
  reportHeading: '이것을 신고하기',
  reportTheName: '이름',
  reportTheBio: '소개',
  reportTheScore: '점수',
  reportWhy: '무엇이 문제인가요? (선택)',
  reportSend: '신고 보내기',
  reportSent: '감사합니다. 누군가 확인할 것입니다.',
  reportSignIn: '신고하려면 로그인하세요.',
  reportGone: '그것은 이제 없습니다.',
  reportCancel: '취소',

  deleteAccount: '내 계정 삭제',
  deleteAccountWhat: '프로필과 보관한 모든 게임이 영구적으로 삭제됩니다. 되돌릴 수 없습니다.',
  deleteAccountAsk: '코드 보내주세요',
  deleteConfirm: '영구적으로 삭제',
  deleteGone: '계정이 삭제되었습니다. 플레이해 주셔서 감사합니다.',
  deleteNoAddress:
    '이 계정에는 확인된 주소가 없어 삭제를 확인할 수 없습니다. 연락 주시면 직접 처리해 드립니다.',

  rulesetFacts: '{letters}의 단어 · {seconds}마다 새 타일',
  plurals: {
    words: { other: '단어 {n}개' },
    rounds: { other: '{n}라운드' },
    flips: { other: '{n}수' },
    ticks: { other: '{n}박자' },
    points: { other: '{n}점' },
    minLetters: { other: '{n}글자 이상' },
    seconds: { other: '{n}초' },
  },
}
